import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ApiRequestError,
  type ApplicationInitializationApp,
  useApiUpdate,
  useCreateApplication,
  useUpdateApplication,
  useUpdateUser,
} from '@access-cms/api-client';
import { User } from '@access-cms/api-client';

import { PrimaryStep, SubStep } from '../config/steps';
import { workflowConfig, WorkflowFormData, WorkflowStep, WorkflowSubStep } from '../config';
import { getNestedValue, hasErrors, validateFields } from '../utils/fieldValidation';
import { FieldErrors } from '../types';
import { extractApplicationMetadata, hasMinimumRequiredData } from '../utils/dataTransformer';
import { getSsnFieldErrorMessage, SSN_IDENTITY_MISMATCH_MESSAGE } from '../utils/ssn-identity-error';
import {
  applicationDataToFormData,
  type ContractEntity,
  extractUserBodyFromFormData,
  type LaborCategoryEntity,
  relatedEntitiesToFormData,
  toPersistedApplicationData,
  userToFormData,
} from './application-map';

import { useAuth } from '@/components/auth/AuthContext';

export enum ApplicationStatus {
  NEW = 'New',
  IN_PROGRESS = 'In Progress',
  SUBMITTED = 'Submitted',
}

export enum WorkflowAlertVariation {
  ERROR = 'error',
  WARN = 'warn',
  SUCCESS = 'success',
}

export interface WorkflowAlert {
  variation: WorkflowAlertVariation;
  heading: string;
  text: string;
}

/** The application and user a save writes to. */
interface SaveTarget {
  applicationId: string;
  userId: string | null;
}

/**
 * ACP-2077: the fallback shown when a sub-step refuses to advance but its
 * `fieldValidation` produced no per-field message. Declared once so raising it and
 * retiring it agree on which alert is ours.
 */
const INCOMPLETE_SUBSTEP_ALERT: WorkflowAlert = {
  heading: 'Some required information is missing',
  text: 'Review this page and complete every required field before continuing.',
  variation: WorkflowAlertVariation.ERROR,
};

interface ApplicationContextType {
  applicationId: string | null;
  applicationStatus: ApplicationStatus;
  setApplicationStatus: (status: ApplicationStatus) => void;
  serverApplicationStatus: string | null;
  isReadOnly: boolean;
  showSuccessAlert: boolean;
  setShowSuccessAlert: (show: boolean) => void;
  dismissSuccessAlert: () => void;

  workflowAlert: WorkflowAlert | null;
  showWorkflowAlert: (alert: WorkflowAlert) => void;
  dismissWorkflowAlert: () => void;
  currentStepIndex: number;
  currentSubStepIndex: number;
  formData: WorkflowFormData;
  completedSubSteps: Set<string>;
  visitedSubSteps: Set<string>;
  highestStepReached: number;
  fieldErrors: FieldErrors;
  isEditMode: boolean;
  editReturnStepIndex: number;
  editReturnSubStepIndex: number;
  editReturnCallback: (() => void) | null;
  hydrateFromServer: (
    app: ApplicationInitializationApp,
    user: User,
    contract?: ContractEntity | null,
    laborCategory?: LaborCategoryEntity | null,
  ) => void;
  prepareNewApplication: (
    contractId: string,
    user: User | null,
    contract?: ContractEntity | null,
    laborCategory?: LaborCategoryEntity | null,
  ) => void;
  isSavingProgress: boolean;
  startWorkflow: () => void;
  viewApplication: () => void;
  closeApplicationView: () => void;
  resetWorkflow: () => void;
  goToNextSubStep: () => Promise<void>;
  completeSubmission: (serverStatus: string) => void;
  goToPreviousSubStep: () => void;
  goToStep: (stepIndex: number, substepIndex: number) => void;
  goToStepForEdit: (
    stepIndex: number,
    substepIndex: number,
    returnStepIndex: number,
    returnSubStepIndex: number,
  ) => void;
  goToStepForCorrection: (stepIndex: number, substepIndex: number) => void;
  saveCurrentProgress: () => Promise<boolean>;
  saveAndReturnToReview: () => Promise<void>;
  cancelEdit: () => void;
  updateSubStepData: (data: Record<string, unknown>) => void;
  updateWorkflowData: (updater: (data: WorkflowFormData) => WorkflowFormData) => void;
  clearSubStepCompletions: (subSteps: Array<{ stepId: PrimaryStep; subStepId: SubStep }>) => void;
  saveAndExit: () => void;
  resetSubSteps: (...indices: number[]) => void;
  currentStep: WorkflowStep | null;
  currentSubStep: WorkflowSubStep | null;
  isFirstSubStep: boolean;
  isLastSubStep: boolean;
  isLastSubStepInCurrentStep: boolean;
  isCurrentStepComplete: boolean;
  totalSteps: number;
  currentStepNumber: number;
  isSignatureModalOpen: boolean;
  openSignatureModal: () => void;
  closeSignatureModal: () => void;
}

export const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);

interface ApplicationProviderProps {
  children: ReactNode;
  initialFormData?: Partial<WorkflowFormData>;
}

/**
 * Initialize default form data from workflow configuration
 */
function initializeFormData(initialFormData?: Partial<WorkflowFormData>): WorkflowFormData {
  const data: WorkflowFormData = {};

  workflowConfig.steps.forEach((step) => {
    data[step.id] = {};
    step.subSteps.forEach((substep) => {
      data[step.id][substep.id] = substep.defaultData || {};
    });
  });

  if (initialFormData) {
    Object.entries(initialFormData).forEach(([stepId, subSteps]) => {
      if (!subSteps) {
        return;
      }

      data[stepId] = {
        ...(data[stepId] ?? {}),
        ...subSteps,
      };
    });
  }

  return data;
}

function mergeFormData(defaults: WorkflowFormData, serverData: Record<string, unknown>): WorkflowFormData {
  const merged: WorkflowFormData = {};
  const typedServerData = serverData as WorkflowFormData;

  for (const stepId of Object.keys(defaults)) {
    merged[stepId] = {};

    for (const substepId of Object.keys(defaults[stepId])) {
      merged[stepId][substepId] = {
        ...defaults[stepId][substepId],
        ...(typedServerData[stepId]?.[substepId] ?? {}),
      };
    }
  }

  return merged;
}

function getSubStepKey(stepId: string, substepId: string): string {
  return `${stepId}:${substepId}`;
}

interface DerivedWorkflowProgress {
  completedSubSteps: Set<string>;
  highestStepReached: number;
  nextStepIndex: number;
  nextSubStepIndex: number;
  visitedSubSteps: Set<string>;
}

function isHydratedSubStepComplete(
  step: WorkflowStep,
  substep: WorkflowSubStep,
  hydratedFormData: WorkflowFormData,
): boolean {
  const substepData = hydratedFormData[step.id]?.[substep.id] || substep.defaultData || {};

  if (substep.fieldValidation) {
    const errors = validateFields(substepData, substep.fieldValidation, hydratedFormData);
    if (hasErrors(errors)) {
      return false;
    }
  }

  return substep.validate ? substep.validate(substepData, hydratedFormData) : true;
}

export function deriveWorkflowProgress(hydratedFormData: WorkflowFormData): DerivedWorkflowProgress {
  const completedSubSteps = new Set<string>();
  let firstIncompleteStepIndex: number | null = null;
  let firstIncompleteSubStepIndex: number | null = null;
  let highestCompletedStepIndex = -1;
  const finalReviewStepIndex = workflowConfig.steps.findIndex((step) => step.id === PrimaryStep.FINAL_REVIEW);
  const lastDataEntryStepIndex = finalReviewStepIndex >= 0 ? finalReviewStepIndex - 1 : workflowConfig.steps.length - 1;

  workflowConfig.steps.forEach((step, stepIndex) => {
    if (stepIndex > lastDataEntryStepIndex) return;

    const visibleSubSteps = step.subSteps
      .map((substep, subStepIndex) => ({ substep, subStepIndex }))
      .filter(({ substep }) => !substep.shouldSkip || !substep.shouldSkip(hydratedFormData));

    let isStepComplete = visibleSubSteps.length > 0;

    visibleSubSteps.forEach(({ substep, subStepIndex }) => {
      const key = getSubStepKey(step.id, substep.id);
      const isComplete = isHydratedSubStepComplete(step, substep, hydratedFormData);

      if (isComplete) {
        completedSubSteps.add(key);
      } else {
        isStepComplete = false;
        if (firstIncompleteStepIndex === null) {
          firstIncompleteStepIndex = stepIndex;
          firstIncompleteSubStepIndex = subStepIndex;
        }
      }
    });

    if (isStepComplete) {
      highestCompletedStepIndex = Math.max(highestCompletedStepIndex, stepIndex);
    }
  });

  const nextStepIndex = firstIncompleteStepIndex ?? Math.max(finalReviewStepIndex, 0);
  const nextSubStepIndex = firstIncompleteSubStepIndex ?? 0;
  const visitedSubSteps = new Set(completedSubSteps);
  const nextStep = workflowConfig.steps[nextStepIndex];
  const nextSubStep = nextStep?.subSteps[nextSubStepIndex];
  if (nextStep && nextSubStep) {
    visitedSubSteps.add(getSubStepKey(nextStep.id, nextSubStep.id));
  }

  return {
    completedSubSteps,
    highestStepReached: Math.max(highestCompletedStepIndex, nextStepIndex),
    nextStepIndex,
    nextSubStepIndex,
    visitedSubSteps,
  };
}

export function ApplicationProvider({ children, initialFormData }: ApplicationProviderProps) {
  const location = useLocation();
  const previousLocationRef = useRef<string>(location.pathname);
  const handledResetLocationKeyRef = useRef<string | null>(null);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [applicationStatus, setApplicationStatus] = useState<ApplicationStatus>(ApplicationStatus.NEW);
  const [serverApplicationStatus, setServerApplicationStatus] = useState<string | null>(null);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [workflowAlert, setWorkflowAlert] = useState<WorkflowAlert | null>(null);
  const [saveExitStatus, setSaveExitStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(-1);
  const [currentSubStepIndex, setCurrentSubStepIndex] = useState<number>(0);
  const [formData, setFormData] = useState<WorkflowFormData>(() => initializeFormData(initialFormData));
  const [completedSubSteps, setCompletedSubSteps] = useState<Set<string>>(new Set());
  const [visitedSubSteps, setVisitedSubSteps] = useState<Set<string>>(new Set());
  const [highestStepReached, setHighestStepReached] = useState<number>(-1);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editReturnStepIndex, setEditReturnStepIndex] = useState<number>(-1);
  const [editReturnSubStepIndex, setEditReturnSubStepIndex] = useState<number>(0);
  const [editReturnCallback, setEditReturnCallback] = useState<(() => void) | null>(null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState<boolean>(false);
  const isCreatingApplicationRef = useRef(false);
  const isSavingExitRef = useRef(false);

  // API hooks for creating and updating applications
  const createApplication = useCreateApplication({
    onError: (error) => {
      console.error('Failed to create application:', error);
      showWorkflowAlert({
        heading: 'Failed to Create Application',
        text: 'There was an error creating your application. Please try again.',
        variation: WorkflowAlertVariation.ERROR,
      });
    },
  });

  const updateUser = useUpdateUser();
  const updateSsn = useApiUpdate({
    path: '/api/v1/users/{id}/ssn',
  });

  const updateApplication = useUpdateApplication({
    onError: (error) => {
      console.error('Failed to save progress:', error);
    },
  });

  const isSavingProgress = updateApplication.isPending || updateUser.isPending || updateSsn.isPending;

  const openSignatureModal = useCallback(() => {
    setIsSignatureModalOpen(true);
  }, []);

  const closeSignatureModal = useCallback(() => {
    setIsSignatureModalOpen(false);
  }, []);

  const dismissSuccessAlert = useCallback(() => {
    setShowSuccessAlert(false);
  }, []);

  const showWorkflowAlert = useCallback((alert: WorkflowAlert) => {
    setWorkflowAlert(alert);
  }, []);

  const dismissWorkflowAlert = useCallback(() => {
    setWorkflowAlert(null);
  }, []);

  useEffect(() => {
    if (showSuccessAlert && location.pathname !== '/application') {
      dismissSuccessAlert();
    }
  }, [location.pathname, showSuccessAlert, dismissSuccessAlert]);

  useEffect(() => {
    if (location.pathname !== '/application' && isEditMode && !editReturnCallback) {
      setIsEditMode(false);
      setEditReturnStepIndex(-1);
      setEditReturnSubStepIndex(0);
      setEditReturnCallback(null);
    }
  }, [location.pathname, isEditMode, editReturnCallback]);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousLocationRef.current;
    const isOnApplicationRoute = currentPath === '/application';
    const wasOnApplicationRoute = previousPath === '/application';
    const isInWorkflow = currentStepIndex >= 0;
    const hasResetRequest = (location.state as { resetWorkflow?: boolean })?.resetWorkflow === true;
    const shouldResetFromState = hasResetRequest && handledResetLocationKeyRef.current !== location.key;

    if (shouldResetFromState) {
      // Navigation state describes a single navigation event. Remember the
      // location even when the workflow is already closed so later step
      // changes cannot replay the same reset request.
      handledResetLocationKeyRef.current = location.key;
    }

    if (wasOnApplicationRoute && !isOnApplicationRoute && isInWorkflow && !isEditMode) {
      setCurrentStepIndex(-1);
      setCurrentSubStepIndex(0);
    }

    if (isOnApplicationRoute && shouldResetFromState && isInWorkflow) {
      setCurrentStepIndex(-1);
      setCurrentSubStepIndex(0);
    }

    previousLocationRef.current = currentPath;
  }, [location.key, location.pathname, location.state, currentStepIndex, isEditMode]);

  const currentStep = useMemo(() => {
    if (currentStepIndex >= 0 && currentStepIndex < workflowConfig.steps.length) {
      return workflowConfig.steps[currentStepIndex];
    }

    return null;
  }, [currentStepIndex]);

  const currentSubStep = useMemo(() => {
    if (currentStep && currentSubStepIndex >= 0 && currentSubStepIndex < currentStep.subSteps.length) {
      return currentStep.subSteps[currentSubStepIndex];
    }

    return null;
  }, [currentStep, currentSubStepIndex]);

  useEffect(() => {
    setFieldErrors({});
  }, [currentStepIndex, currentSubStepIndex]);

  useEffect(() => {
    if (currentStepIndex < 0) return;
    setHighestStepReached((prev) => Math.max(prev, currentStepIndex));
  }, [currentStepIndex]);

  useEffect(() => {
    if (!currentStep || !currentSubStep) return;
    const key = getSubStepKey(currentStep.id, currentSubStep.id);
    setVisitedSubSteps((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);

      return next;
    });
  }, [currentStep, currentSubStep]);
  const isCurrentStepComplete = useMemo(() => {
    if (!currentStep) return false;

    return currentStep.subSteps.every((substep) => {
      const key = getSubStepKey(currentStep.id, substep.id);

      return completedSubSteps.has(key);
    });
  }, [currentStep, completedSubSteps]);

  const isFirstSubStep = currentStepIndex === 0 && currentSubStepIndex === 0;

  const isLastSubStepInCurrentStep = useMemo(() => {
    if (!currentStep) return false;

    // Find the last non-skipped substep in the current step
    let lastVisibleSubStepIndex = -1;
    for (let i = currentStep.subSteps.length - 1; i >= 0; i--) {
      const substep = currentStep.subSteps[i];
      if (!substep.shouldSkip || !substep.shouldSkip(formData)) {
        lastVisibleSubStepIndex = i;
        break;
      }
    }

    return currentSubStepIndex === lastVisibleSubStepIndex;
  }, [currentStep, currentSubStepIndex, formData]);

  const isLastSubStep = useMemo(() => {
    if (!currentStep) return false;

    return (
      currentStepIndex === workflowConfig.steps.length - 1 && currentSubStepIndex === currentStep.subSteps.length - 1
    );
  }, [currentStep, currentStepIndex, currentSubStepIndex]);

  const hydrateFromServer = useCallback(
    (
      app: ApplicationInitializationApp,
      user: User,
      contract?: ContractEntity | null,
      laborCategory?: LaborCategoryEntity | null,
    ) => {
      setApplicationId(app.id);
      setUserId(app.userId);

      setServerApplicationStatus(app.applicationStatus);

      // Any returned variant (COR / FNMS / VAS) re-opens the workflow for
      // edits and resubmission, same as a draft — as does a VAS
      // fingerprints/eApp region timeout (applicationSubStatus), which can
      // leave the app at VAS_AWAITING_FINGERPRINTS_OR_EAPP (pre-escalation) or
      // VAS_TIMEOUT (case cancelled). A healthy in-flight verification or a
      // plain 14-day full-case VAS_TIMEOUT (no region marker) stays read-only.
      const editableStatuses = ['DRAFT', 'RETURNED', 'FNMS_RETURNED', 'VAS_RETURNED'];
      const hasVasRegionTimeout =
        app.applicationSubStatus?.fingerprints === 'timeout' || app.applicationSubStatus?.eapp === 'timeout';
      const isVasTimeoutResubmittable =
        (app.applicationStatus === 'VAS_AWAITING_FINGERPRINTS_OR_EAPP' || app.applicationStatus === 'VAS_TIMEOUT') &&
        hasVasRegionTimeout;
      if (editableStatuses.includes(app.applicationStatus) || isVasTimeoutResubmittable) {
        setApplicationStatus(ApplicationStatus.IN_PROGRESS);
      } else {
        setApplicationStatus(ApplicationStatus.SUBMITTED);
      }

      if (app.applicationData) {
        const defaults = initializeFormData();
        const userFormFields = userToFormData(user);
        const mergedUser = mergeFormData(defaults, { [PrimaryStep.PERSONAL_DETAILS]: userFormFields });
        const apiFormData = applicationDataToFormData(app.applicationData as Record<string, unknown>);
        const merged = mergeFormData(mergedUser, apiFormData);
        const relatedEntityData = relatedEntitiesToFormData(contract ?? null, laborCategory ?? null);
        const mergedWithEntities = mergeFormData(merged, relatedEntityData);
        const derivedProgress = deriveWorkflowProgress(mergedWithEntities);
        setFormData(mergedWithEntities);
        setCompletedSubSteps(derivedProgress.completedSubSteps);
        setVisitedSubSteps(derivedProgress.visitedSubSteps);
        setHighestStepReached(derivedProgress.highestStepReached);
      }
    },
    [],
  );

  /**
   * Seed the form for a contract the user has no application on yet (a
   * roster-only selection from the multi-contract picker). The application
   * itself is created by the workflow's POST on the first Continue; this
   * supplies the contractId/laborCategoryId that create call needs, plus the
   * user's profile, matching what hydrateFromServer seeds for a server-created
   * draft.
   */
  const prepareNewApplication = useCallback(
    (
      contractId: string,
      user: User | null,
      contract?: ContractEntity | null,
      laborCategory?: LaborCategoryEntity | null,
    ) => {
      setApplicationId(null);
      setUserId(user?.id ?? null);

      const defaults = initializeFormData(initialFormData);
      const withUser = user
        ? mergeFormData(defaults, { [PrimaryStep.PERSONAL_DETAILS]: userToFormData(user) })
        : defaults;
      const relatedEntityData = relatedEntitiesToFormData(contract ?? null, laborCategory ?? null);
      const withEntities = mergeFormData(withUser, relatedEntityData);
      // Read by extractApplicationMetadata for the create call; never persisted
      // in applicationData.
      const creationIds = {
        [PrimaryStep.CONTRACT_DETAILS]: {
          [SubStep.CONTRACT_INFO]: {
            contractId,
            ...(laborCategory?.id ? { laborCategoryId: laborCategory.id } : {}),
          },
        },
      };
      setFormData(mergeFormData(withEntities, creationIds));
    },
    [initialFormData],
  );

  const startWorkflow = useCallback(() => {
    const derivedProgress = deriveWorkflowProgress(formData);
    dismissSuccessAlert();
    setSaveExitStatus('idle');
    setIsReadOnly(false);
    setCompletedSubSteps(derivedProgress.completedSubSteps);
    setVisitedSubSteps(derivedProgress.visitedSubSteps);
    setCurrentStepIndex(derivedProgress.nextStepIndex);
    setCurrentSubStepIndex(derivedProgress.nextSubStepIndex);
    setHighestStepReached(derivedProgress.highestStepReached);
    setApplicationStatus(ApplicationStatus.IN_PROGRESS);
  }, [dismissSuccessAlert, formData]);

  const viewApplication = useCallback(() => {
    dismissSuccessAlert();
    setIsReadOnly(true);
    const finalReviewIndex = workflowConfig.steps.findIndex((s) => s.id === PrimaryStep.FINAL_REVIEW);
    setCurrentStepIndex(finalReviewIndex);
    setCurrentSubStepIndex(0);
    setHighestStepReached(finalReviewIndex);
  }, [dismissSuccessAlert]);

  // Leaving the read-only view must preserve the hydrated application for
  // the landing page and subsequent views. resetWorkflow clears that identity.
  const closeApplicationView = useCallback(() => {
    setCurrentStepIndex(-1);
    setCurrentSubStepIndex(0);
    setIsReadOnly(false);
  }, []);

  const resetWorkflow = useCallback(() => {
    setApplicationId(null);
    setUserId(null);
    setApplicationStatus(ApplicationStatus.NEW);
    setServerApplicationStatus(null);
    setShowSuccessAlert(false);
    setWorkflowAlert(null);
    setCurrentStepIndex(-1);
    setCurrentSubStepIndex(0);
    setIsReadOnly(false);
    setFormData(initializeFormData(initialFormData));
    setCompletedSubSteps(new Set());
    setVisitedSubSteps(new Set());
    setHighestStepReached(-1);
    setFieldErrors({});
    setIsEditMode(false);
    setEditReturnStepIndex(-1);
    setEditReturnSubStepIndex(0);
    setEditReturnCallback(null);
    setIsSignatureModalOpen(false);
    setSaveExitStatus('idle');
    isCreatingApplicationRef.current = false;
    isSavingExitRef.current = false;
  }, [initialFormData]);

  const { user } = useAuth();
  const isCmsIdmUser = user?.source_type === 'cmsidm';
  const isBasicInfoSubStep =
    currentStep?.id === PrimaryStep.PERSONAL_DETAILS && currentSubStep?.id === SubStep.BASIC_INFO;

  /**
   * Centralized save logic that separates user-owned data from application data.
   * User-owned substeps (basicInfo, personalAddress) are sent via updateUser;
   * all other data is sent via updateApplication with user substeps cleared.
   * cmsidm SSN identity check runs only on Basic Information.
   */
  const saveProgress = useCallback(
    async (dataToSave: WorkflowFormData = formData, target?: SaveTarget): Promise<boolean> => {
      const targetApplicationId = target?.applicationId ?? applicationId;
      const targetUserId = target?.userId ?? userId;

      if (!targetApplicationId) return false;

      const metadata = extractApplicationMetadata(dataToSave);
      const { userBody, ssn } = extractUserBodyFromFormData(dataToSave);
      const applicationData = toPersistedApplicationData(dataToSave);

      if (targetUserId && typeof ssn === 'string') {
        if (isCmsIdmUser && isBasicInfoSubStep) {
          try {
            await updateSsn.mutateAsync({
              body: { ssn },
              pathParams: { id: targetUserId },
            });
          } catch (error) {
            if (!(error instanceof ApiRequestError && error.statusCode === 409)) {
              setFieldErrors({
                ssn: error instanceof ApiRequestError ? getSsnFieldErrorMessage(error) : SSN_IDENTITY_MISMATCH_MESSAGE,
              });

              return false;
            }
          }
        } else if (!isCmsIdmUser) {
          updateSsn.mutate({
            body: { ssn },
            pathParams: { id: targetUserId },
          });
        }
      }

      if (targetUserId) {
        updateUser.mutate({
          body: userBody,
          pathParams: { id: targetUserId },
        });
      }

      try {
        await updateApplication.mutateAsync({
          body: {
            applicationData,
            applicationType: metadata.applicationType,
          },
          pathParams: { id: targetApplicationId },
        });
      } catch {
        showWorkflowAlert({
          heading: 'Failed to Save',
          text: 'Your application could not be saved. Please try again.',
          variation: WorkflowAlertVariation.ERROR,
        });

        return false;
      }

      return true;
    },
    [
      applicationId,
      userId,
      isCmsIdmUser,
      isBasicInfoSubStep,
      formData,
      updateUser,
      updateApplication,
      updateSsn,
      showWorkflowAlert,
    ],
  );

  /**
   * Create the draft for a contract selected without an application. Returns
   * the new ids so the caller can save this step against them right away;
   * the state set here is not visible to the caller's closure until the next
   * render. Returns null when the create fails or one is already in flight.
   */
  const createDraftApplication = useCallback(
    async (dataToSave: WorkflowFormData): Promise<SaveTarget | null> => {
      const metadata = extractApplicationMetadata(dataToSave);
      if (!metadata.contractId || !metadata.laborCategoryId) return null;
      if (isCreatingApplicationRef.current) return null;

      isCreatingApplicationRef.current = true;
      try {
        const response = await createApplication.mutateAsync({
          applicationData: toPersistedApplicationData(dataToSave),
          applicationType: metadata.applicationType,
          contractId: metadata.contractId,
          laborCategoryId: metadata.laborCategoryId,
        });
        const created = response.data;
        if (!created?.id) return null;

        const createdUserId = created.userId ?? userId;
        setApplicationId(created.id);
        setUserId(createdUserId);
        setServerApplicationStatus(created.applicationStatus ?? 'DRAFT');

        return { applicationId: created.id, userId: createdUserId };
      } catch {
        // useCreateApplication's onError has already raised the alert.
        return null;
      } finally {
        isCreatingApplicationRef.current = false;
      }
    },
    [createApplication, userId],
  );

  const saveCurrentProgress = useCallback(async () => {
    const saved = await saveProgress();
    if (!saved) return false;
    setWorkflowAlert(null);

    return true;
  }, [saveProgress]);

  // Heading of the step-level alert this context raised for a blocked advance — the
  // generic one or a sub-step's blockedReason — so retiring it leaves other alerts alone.
  const blockedAlertHeadingRef = useRef<string | null>(null);

  /** Retires the fallback alert only, leaving alerts raised for other reasons alone. */
  const clearIncompleteSubStepAlert = useCallback(() => {
    const heading = blockedAlertHeadingRef.current;
    setWorkflowAlert((current) =>
      current && (current.heading === INCOMPLETE_SUBSTEP_ALERT.heading || current.heading === heading) ? null : current,
    );
  }, []);

  /**
   * ACP-2077: feedback for a blocked advance. When the per-field map produced
   * messages the fields speak for themselves, so any stale step-level alert is
   * cleared; when it produced none the sub-step would block with nothing to read,
   * so a step-level alert explains it instead.
   */
  const announceIncompleteSubStep = useCallback(
    (hasFieldMessages: boolean, reason?: { heading: string; text: string }, keepAlert = false) => {
      if (hasFieldMessages && !keepAlert) {
        clearIncompleteSubStepAlert();

        return;
      }

      // ACP-2092: a sub-step that knows why it is blocked says so; the generic alert
      // would send the user looking for a missing field that is not there.
      const alert = reason ? { ...reason, variation: WorkflowAlertVariation.ERROR } : { ...INCOMPLETE_SUBSTEP_ALERT };
      blockedAlertHeadingRef.current = alert.heading;
      setWorkflowAlert(alert);
    },
    [clearIncompleteSubStepAlert],
  );

  const goToNextSubStep = useCallback(async () => {
    if (!currentStep || !currentSubStep) return;

    let substepData = formData[currentStep.id]?.[currentSubStep.id] || {};
    let dataForSave = formData;
    let errors: Record<string, string | undefined> = {};

    if (currentSubStep.fieldValidation) {
      errors = validateFields(substepData, currentSubStep.fieldValidation, formData);
    }

    const isSubStepValid = currentSubStep.validate ? currentSubStep.validate(substepData, formData) : true;

    if (hasErrors(errors) || !isSubStepValid) {
      setFieldErrors(errors);
      // ACP-2076/2077 (Section 508): the attempt must never look like it did nothing.
      // Field-level messages come from `fieldValidation`; a sub-step whose `validate`
      // rejects something that map does not describe would otherwise block silently,
      // so it falls back to a step-level alert (role="alert", announced assertively).
      announceIncompleteSubStep(
        hasErrors(errors),
        currentSubStep.blockedReason?.(substepData, formData),
        currentSubStep.keepIncompleteAlert,
      );

      return;
    }

    // The attempt succeeded, so retire any fallback alert a previous one raised —
    // otherwise it would follow the user into the next sub-step.
    clearIncompleteSubStepAlert();

    if (currentSubStep.onBeforeNext) {
      substepData = currentSubStep.onBeforeNext(substepData, formData);
      dataForSave = {
        ...formData,
        [currentStep.id]: {
          ...formData[currentStep.id],
          [currentSubStep.id]: substepData,
        },
      };
      setFormData((prev) => ({
        ...prev,
        [currentStep.id]: {
          ...prev[currentStep.id],
          [currentSubStep.id]: substepData,
        },
      }));
    }

    // A contract selected without an application gets its draft created on
    // the first Continue; this step is then saved against it like any other.
    let saveTarget: SaveTarget | null = applicationId ? { applicationId, userId } : null;

    if (!saveTarget && hasMinimumRequiredData(dataForSave)) {
      saveTarget = await createDraftApplication(dataForSave);
      if (!saveTarget) {
        return;
      }
    }

    if (saveTarget) {
      const saved = await saveProgress(dataForSave, saveTarget);
      if (!saved) {
        return;
      }
    }

    setFieldErrors({});
    const key = getSubStepKey(currentStep.id, currentSubStep.id);
    setCompletedSubSteps((prev) => new Set(prev).add(key));

    const findNextVisibleSubStep = (
      stepIdx: number,
      substepIdx: number,
    ): { stepIndex: number; substepIndex: number } | null => {
      const step = workflowConfig.steps[stepIdx];

      for (let i = substepIdx + 1; i < step.subSteps.length; i++) {
        const substep = step.subSteps[i];

        if (!substep.shouldSkip || !substep.shouldSkip(formData)) {
          return { stepIndex: stepIdx, substepIndex: i };
        } else {
          const substepKey = getSubStepKey(step.id, substep.id);
          setCompletedSubSteps((prev) => new Set(prev).add(substepKey));
        }
      }

      if (stepIdx < workflowConfig.steps.length - 1) {
        const nextStep = workflowConfig.steps[stepIdx + 1];

        for (let i = 0; i < nextStep.subSteps.length; i++) {
          const substep = nextStep.subSteps[i];

          if (!substep.shouldSkip || !substep.shouldSkip(formData)) {
            setHighestStepReached((prev) => Math.max(prev, stepIdx + 1));

            return { stepIndex: stepIdx + 1, substepIndex: i };
          } else {
            const substepKey = getSubStepKey(nextStep.id, substep.id);
            setCompletedSubSteps((prev) => new Set(prev).add(substepKey));
          }
        }

        return findNextVisibleSubStep(stepIdx + 1, -1);
      }

      return null;
    };

    const next = findNextVisibleSubStep(currentStepIndex, currentSubStepIndex);

    if (next) {
      setCurrentStepIndex(next.stepIndex);
      setCurrentSubStepIndex(next.substepIndex);
    }
  }, [
    currentStep,
    currentSubStep,
    currentStepIndex,
    currentSubStepIndex,
    formData,
    applicationId,
    userId,
    createDraftApplication,
    saveProgress,
  ]);

  const completeSubmission = useCallback(
    (serverStatus: string) => {
      dismissSuccessAlert();
      setWorkflowAlert(null);
      setFieldErrors({});
      setServerApplicationStatus(serverStatus);
      setApplicationStatus(ApplicationStatus.SUBMITTED);
      setIsReadOnly(true);
      setIsSignatureModalOpen(false);

      const confirmationStepIndex = workflowConfig.steps.findIndex((step) => step.id === PrimaryStep.CONFIRMATION);
      if (confirmationStepIndex < 0) {
        return;
      }

      const finalReviewStep = workflowConfig.steps.find((step) => step.id === PrimaryStep.FINAL_REVIEW);
      if (finalReviewStep) {
        setCompletedSubSteps((prev) => {
          const next = new Set(prev);
          finalReviewStep.subSteps.forEach((substep) => {
            next.add(getSubStepKey(finalReviewStep.id, substep.id));
          });

          return next;
        });
      }

      setHighestStepReached(confirmationStepIndex);
      setCurrentStepIndex(confirmationStepIndex);
      setCurrentSubStepIndex(0);
    },
    [dismissSuccessAlert],
  );

  const goToPreviousSubStep = useCallback(() => {
    if (!currentStep || !currentSubStep) return;

    const substepData = formData[currentStep.id]?.[currentSubStep.id] || {};
    let isValid = true;

    if (currentSubStep.fieldValidation) {
      const errors = validateFields(substepData, currentSubStep.fieldValidation, formData);
      isValid = !hasErrors(errors);
    }

    if (isValid && currentSubStep.validate) {
      isValid = currentSubStep.validate(substepData, formData);
    }

    if (isValid) {
      const key = getSubStepKey(currentStep.id, currentSubStep.id);
      setCompletedSubSteps((prev) => new Set(prev).add(key));
    }

    setFieldErrors({});

    const findPreviousVisibleSubStep = (
      stepIdx: number,
      substepIdx: number,
    ): { stepIndex: number; substepIndex: number } | null => {
      for (let i = substepIdx - 1; i >= 0; i--) {
        const step = workflowConfig.steps[stepIdx];
        const substep = step.subSteps[i];

        if (!substep.shouldSkip || !substep.shouldSkip(formData)) {
          return { stepIndex: stepIdx, substepIndex: i };
        }
      }

      if (stepIdx > 0) {
        const prevStep = workflowConfig.steps[stepIdx - 1];

        for (let i = prevStep.subSteps.length - 1; i >= 0; i--) {
          const substep = prevStep.subSteps[i];

          if (!substep.shouldSkip || !substep.shouldSkip(formData)) {
            return { stepIndex: stepIdx - 1, substepIndex: i };
          }
        }

        return findPreviousVisibleSubStep(stepIdx - 1, 0);
      }

      return null;
    };

    const previous = findPreviousVisibleSubStep(currentStepIndex, currentSubStepIndex);

    if (previous) {
      setCurrentStepIndex(previous.stepIndex);
      setCurrentSubStepIndex(previous.substepIndex);
    }
  }, [currentStep, currentSubStep, currentStepIndex, currentSubStepIndex, formData]);

  const goToStep = useCallback((stepIndex: number, substepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < workflowConfig.steps.length) {
      const step = workflowConfig.steps[stepIndex];
      if (substepIndex >= 0 && substepIndex < step.subSteps.length) {
        setHighestStepReached((prev) => Math.max(prev, stepIndex));
        setCurrentStepIndex(stepIndex);
        setCurrentSubStepIndex(substepIndex);
      }
    }
  }, []);

  const goToStepForEdit = useCallback(
    (stepIndex: number, substepIndex: number, returnStepIndex: number, returnSubStepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < workflowConfig.steps.length) {
        const step = workflowConfig.steps[stepIndex];
        if (substepIndex >= 0 && substepIndex < step.subSteps.length) {
          setIsEditMode(true);
          setEditReturnStepIndex(returnStepIndex);
          setEditReturnSubStepIndex(returnSubStepIndex);
          setEditReturnCallback(null);
          setCurrentStepIndex(stepIndex);
          setCurrentSubStepIndex(substepIndex);
        }
      }
    },
    [],
  );

  /**
   * Enter the FULL workflow jumped to a specific step/substep — for correcting
   * a returned application from the status tracker's per-field Edit links.
   *
   * Deliberately NOT edit mode: edit mode is the spot-edit affordance (Cancel/
   * Save, step navigation locked, Save returns to where you came from) used
   * from the Final Review page. A returned application needs the opposite —
   * the applicant lands on the flagged section but keeps normal Back/Continue
   * navigation so they can walk forward to Final Review, re-sign, and
   * resubmit.
   */
  const goToStepForCorrection = useCallback(
    (stepIndex: number, substepIndex: number) => {
      if (stepIndex < 0 || stepIndex >= workflowConfig.steps.length) return;
      const step = workflowConfig.steps[stepIndex];
      if (substepIndex < 0 || substepIndex >= step.subSteps.length) return;

      const derivedProgress = deriveWorkflowProgress(formData);
      dismissSuccessAlert();
      setSaveExitStatus('idle');
      setIsReadOnly(false);
      setIsEditMode(false);
      setEditReturnCallback(null);
      setCompletedSubSteps(derivedProgress.completedSubSteps);
      setVisitedSubSteps(derivedProgress.visitedSubSteps);
      setHighestStepReached(Math.max(derivedProgress.highestStepReached, stepIndex));
      setCurrentStepIndex(stepIndex);
      setCurrentSubStepIndex(substepIndex);
      setApplicationStatus(ApplicationStatus.IN_PROGRESS);
    },
    [dismissSuccessAlert, formData],
  );

  const saveAndReturnToReview = useCallback(async () => {
    if (!currentStep || !currentSubStep) return;

    const substepData = formData[currentStep.id]?.[currentSubStep.id] || {};
    let errors: Record<string, string | undefined> = {};

    if (currentSubStep.fieldValidation) {
      errors = validateFields(substepData, currentSubStep.fieldValidation, formData);
    }

    const isSubStepValid = currentSubStep.validate ? currentSubStep.validate(substepData, formData) : true;

    if (hasErrors(errors) || !isSubStepValid) {
      setFieldErrors(errors);
      announceIncompleteSubStep(
        hasErrors(errors),
        currentSubStep.blockedReason?.(substepData, formData),
        currentSubStep.keepIncompleteAlert,
      );

      return;
    }

    // Same as the Next path: a satisfied sub-step must not carry the fallback alert
    // forward.
    clearIncompleteSubStepAlert();

    const saved = await saveProgress();
    if (!saved) {
      return;
    }

    setFieldErrors({});

    const key = getSubStepKey(currentStep.id, currentSubStep.id);
    setCompletedSubSteps((prev) => new Set(prev).add(key));

    if (editReturnCallback) {
      editReturnCallback();
      setEditReturnCallback(null);
    } else {
      setCurrentStepIndex(editReturnStepIndex);
      setCurrentSubStepIndex(editReturnSubStepIndex);
    }

    setIsEditMode(false);
    setEditReturnStepIndex(-1);
    setEditReturnSubStepIndex(0);
  }, [
    currentStep,
    currentSubStep,
    formData,
    editReturnStepIndex,
    editReturnSubStepIndex,
    editReturnCallback,
    saveProgress,
  ]);

  const cancelEdit = useCallback(() => {
    if (editReturnCallback) {
      editReturnCallback();
      setEditReturnCallback(null);
    } else {
      setCurrentStepIndex(editReturnStepIndex);
      setCurrentSubStepIndex(editReturnSubStepIndex);
    }

    setIsEditMode(false);
    setEditReturnStepIndex(-1);
    setEditReturnSubStepIndex(0);
    setFieldErrors({});
  }, [editReturnStepIndex, editReturnSubStepIndex, editReturnCallback]);

  const updateSubStepData = useCallback(
    (data: Record<string, unknown>) => {
      if (!currentStep || !currentSubStep) return;

      setFormData((prev) => {
        const prevSubStep = prev[currentStep.id]?.[currentSubStep.id] as Record<string, unknown> | undefined;

        // ACP-2092 (Section 508): a corrected field has to stop reporting itself as
        // invalid, or a screen reader user re-reading the form hears "invalid" on
        // fields they already fixed. Re-validating rather than clearing on any
        // keystroke means the message only goes away once the value is genuinely
        // acceptable — it never hides a real error. This used to cover `ssn` alone.
        if (currentSubStep.fieldValidation) {
          const stillFailing = validateFields(data, currentSubStep.fieldValidation, prev);

          setFieldErrors((current) => {
            // Error keys follow the same dot notation as the validation config
            // (`draftMilitaryExperience.branch`), so the before/after comparison has
            // to resolve the path — indexing directly reads undefined on both sides
            // and the field would never clear.
            const valueOf = (source: Record<string, unknown> | undefined, field: string) =>
              source && field.includes('.') ? getNestedValue(source, field) : source?.[field];

            const cleared = Object.keys(current).filter(
              (field) => current[field] && !stillFailing[field] && valueOf(prevSubStep, field) !== valueOf(data, field),
            );

            if (cleared.length === 0) {
              return current;
            }

            const next = { ...current };
            cleared.forEach((field) => delete next[field]);

            return next;
          });
        }

        return {
          ...prev,
          [currentStep.id]: {
            ...prev[currentStep.id],
            [currentSubStep.id]: data,
          },
        };
      });
    },
    [currentStep, currentSubStep],
  );

  const updateWorkflowData = useCallback((updater: (data: WorkflowFormData) => WorkflowFormData) => {
    setFormData(updater);
  }, []);

  const clearSubStepCompletions = useCallback((subSteps: Array<{ stepId: PrimaryStep; subStepId: SubStep }>) => {
    setCompletedSubSteps((current) => {
      const next = new Set(current);
      subSteps.forEach(({ stepId, subStepId }) => next.delete(getSubStepKey(stepId, subStepId)));

      return next;
    });
  }, []);

  const saveAndExit = useCallback(() => {
    if (!applicationId) return;
    if (isSavingExitRef.current) return;

    // Reset previous mutation states to avoid stale errors bleeding through
    updateSsn.reset();
    updateUser.reset();
    updateApplication.reset();

    isSavingExitRef.current = true;
    saveProgress();
    setSaveExitStatus('saving');
  }, [applicationId, saveProgress, updateSsn, updateUser, updateApplication]);

  // Monitor save-and-exit mutations — when all settle, resolve success or failure
  useEffect(() => {
    if (saveExitStatus !== 'saving') return;

    const allSettled = !updateSsn.isPending && !updateUser.isPending && !updateApplication.isPending;
    if (!allSettled) return;

    isSavingExitRef.current = false;

    // TODO: 409 Conflict from updateSsn indicates SSN existing SSN (not an error)
    // which we treat as a success for save-and-exit to avoid blocking the user.
    if ((updateSsn.isError && updateSsn.error.statusCode !== 409) || updateUser.isError || updateApplication.isError) {
      setSaveExitStatus('error');
      showWorkflowAlert({
        heading: 'Failed to Save',
        text: 'Your application could not be saved. Please try again.',
        variation: WorkflowAlertVariation.ERROR,
      });
    } else {
      setSaveExitStatus('success');
      setShowSuccessAlert(true);
      setCurrentStepIndex(-1);
      setCurrentSubStepIndex(0);
    }
  }, [
    saveExitStatus,
    updateSsn.isPending,
    updateUser.isPending,
    updateApplication.isPending,
    updateSsn.error,
    updateSsn.isError,
    updateUser.isError,
    updateApplication.isError,
    showWorkflowAlert,
    setShowSuccessAlert,
  ]);

  const resetSubSteps = useCallback(
    (...indices: number[]) => {
      if (!currentStep || indices.length === 0) return;

      setCompletedSubSteps((prev) => {
        const newSet = new Set(prev);

        for (const index of indices) {
          if (index >= 0 && index < currentStep.subSteps.length) {
            const substep = currentStep.subSteps[index];
            const key = getSubStepKey(currentStep.id, substep.id);
            newSet.delete(key);
          }
        }

        return newSet;
      });

      setFormData((prev) => {
        const updatedStepData = { ...prev[currentStep.id] };

        for (const index of indices) {
          if (index >= 0 && index < currentStep.subSteps.length) {
            const substep = currentStep.subSteps[index];
            updatedStepData[substep.id] = substep.defaultData || {};
          }
        }

        return {
          ...prev,
          [currentStep.id]: updatedStepData,
        };
      });
    },
    [currentStep],
  );

  const totalSteps = workflowConfig.steps.length;
  const currentStepNumber = currentStepIndex + 1;

  const value: ApplicationContextType = {
    applicationId,
    applicationStatus,
    cancelEdit,
    clearSubStepCompletions,
    closeApplicationView,
    closeSignatureModal,
    completedSubSteps,
    completeSubmission,
    currentStep,
    currentStepIndex,
    currentStepNumber,
    currentSubStep,
    currentSubStepIndex,
    dismissSuccessAlert,
    dismissWorkflowAlert,
    editReturnCallback,
    editReturnStepIndex,
    editReturnSubStepIndex,
    fieldErrors,
    formData,
    goToNextSubStep,
    goToPreviousSubStep,
    goToStep,
    goToStepForCorrection,
    goToStepForEdit,
    highestStepReached,
    hydrateFromServer,
    isCurrentStepComplete,
    isEditMode,
    isFirstSubStep,
    isLastSubStep,
    isLastSubStepInCurrentStep,
    isReadOnly,
    isSavingProgress,
    isSignatureModalOpen,
    openSignatureModal,
    prepareNewApplication,
    resetSubSteps,
    resetWorkflow,
    saveAndExit,
    saveAndReturnToReview,
    saveCurrentProgress,
    serverApplicationStatus,
    setApplicationStatus,
    setShowSuccessAlert,
    showSuccessAlert,
    showWorkflowAlert,
    startWorkflow,
    totalSteps,
    updateSubStepData,
    updateWorkflowData,
    viewApplication,
    visitedSubSteps,
    workflowAlert,
  };

  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
}

export function useApplication() {
  const context = useContext(ApplicationContext);
  if (context === undefined) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        'useApplication: ApplicationContext is undefined. This can happen during hot module reload. ' +
          'If this persists, ensure the component is wrapped in <ApplicationProvider>.',
      );
    }
    throw new Error('useApplication must be used within an ApplicationProvider');
  }

  return context;
}
